import { useEffect } from 'react';
import { NetworkListRequest, NetworkListResponse, getNetworkList } from '@shory-health-individual/customer-api';
import { useAppPlatformContextOptions } from '../../../../contexts/app';
import { useCountries, useFacilityTypes } from '../../../app-init-data';
import { useNetworkListForm } from './network-search-filter/useNetworkListForm';

type PlanNetwork = {
	planId: number;
	refetchNetworkList: () => Promise<void> | undefined;
};

export const useNetworkList = ({ planId, refetchNetworkList }: PlanNetwork) => {
	const { apiConnectionOptions } = useAppPlatformContextOptions();
	const { getFacilityById } = useFacilityTypes();
	const { getCountryById } = useCountries();
	const { planCities, fields, form, isNetworkListNotAvailable } = useNetworkListForm(planId);
	const getCityById = (cityId: number) => planCities?.find((city) => city.id === cityId);
	const { facilityName, facilityTypeIds, cityIds, countryIds } = form.watchValues();
	const searchCriteria = JSON.stringify({ planCities, facilityName, facilityTypeIds, cityIds, countryIds });

	useEffect(() => {
		if (planCities || facilityName || facilityTypeIds || cityIds || countryIds) {
			refetchNetworkList();
		}
	}, [searchCriteria]);

	const mapNetworkList = (network: NetworkListResponse) =>
		network?.network?.map((network) => {
			return {
				facilityName: network?.facilityName ?? '',
				facilityType: getFacilityById(network.facilityTypeId)?.name ?? '',
				facilityRegion: network?.facilityRegion ?? '',
				facilityCity: getCityById(network?.cityId)?.name ?? '',
				facilityCountry: getCountryById(getCityById(network?.cityId)?.countryId ?? 0)?.name ?? '',
			};
		}) ?? [];

	const onFetchNetworkList = async (payload: NetworkListRequest) => {
		const networkSearch = { ...payload, facilityTypeIds, facilityName, cityIds, countryIds };
		const networkList = await getNetworkList(apiConnectionOptions)({ parameters: { planId }, body: networkSearch });

		return {
			data: mapNetworkList(networkList),
			paging: networkList?.paging.totalRecords,
		};
	};

	return {
		onFetchNetworkList,
		planSearchFields: fields,
		networkForm: form,
		isNetworkListNotAvailable,
		isPlanCitiesAvailable: planCities !== undefined,
	};
};
